/**
 * Fee calculation engine and shared constants for the Optimum
 * Fees Calculator. Ported verbatim from the source artifact —
 * do not change the calculation logic, decision-tree text, or
 * fee rates without a deliberate spec update.
 */

import { formatLongDate } from '../../../utils/dateHelpers';

// --- Defaults & constants -------------------------------------------------
export const DEFAULTS = {
  hourlyRate: 1040,        // ZAR per hour
  minThreshold: 100000,    // ZAR — no initial fee on lump sums below this
  feeCap: 0.01,            // 1% of investment, excl. VAT
  vatRate: 0.15,           // 15%
};

// Ongoing advice fee — rates and the split rule (all ex-VAT).
// The standard ongoing rate is 0.25% p.a. When the adviser's split is
// below ONGOING_SPLIT_THRESHOLD_PCT the rate is bumped to 0.50% — unless
// the adviser's share of a 0.25% fee already pays for
// ONGOING_RETAIN_HOURS_PER_MONTH hour(s) of work per month at the
// settings hourly rate, in which case 0.25% is retained. The measure is
// hard-wired here on purpose: there is no settings column for it.
// See resolveOngoingRate() below and CLAUDE.md §38.
export const ONGOING_RATE_STANDARD = 0.0025;   // 0.25% p.a., excl. VAT
export const ONGOING_RATE_BUMPED = 0.005;      // 0.50% p.a., excl. VAT
// Display strings are fixed literals (not fmtPct) so the UI keeps the exact
// "0.25%" / "0.50%" wording regardless of locale decimal separators.
export const ONGOING_RATE_STANDARD_DISPLAY = "0.25%";
export const ONGOING_RATE_BUMPED_DISPLAY = "0.50%";
export const ONGOING_SPLIT_THRESHOLD_PCT = 70; // split at or above this → standard rate
export const ONGOING_RETAIN_HOURS_PER_MONTH = 1;

// Reason codes returned by resolveOngoingRate(). The UI branches on these,
// never on the wording of the reason text.
export const ONGOING_REASON = {
  STANDARD: "standard",  // split >= threshold
  BUMPED: "bumped",      // split < threshold and share below the hourly rate
  RETAINED: "retained",  // split < threshold but share covers one hour per month
};

export const SPECIAL_FUNDS = {
  NONE: "none",
  INCOME: "bci-income",
  EDGE: "oig-edge",
};

export const SPECIAL_FUND_RATES = {
  [SPECIAL_FUNDS.INCOME]: { rate: 0.001, label: "Optimum BCI Income Fund (100%)", display: "0.10%" },
  [SPECIAL_FUNDS.EDGE]: { rate: 0.0015, label: "OIG Capital Edge Model Portfolio (100%)", display: "0.15%" },
};

// Regulated transfers and other initial-fee exemptions.
export const TRANSFER_TYPES = {
  NONE: "none",                // new investment / new money
  SECTION14: "section14",      // Pension Funds Act s.14 — RA transfer
  DIRECTIVE135: "dir135",      // FSCA Directive 135 — Living Annuity transfer
  EXISTING: "existing",        // Appointment on an existing investment — no new money
};

export const TRANSFER_LABELS = {
  [TRANSFER_TYPES.SECTION14]: "Section 14 transfer (Retirement Annuity)",
  [TRANSFER_TYPES.DIRECTIVE135]: "Directive 135 transfer (Living Annuity)",
  [TRANSFER_TYPES.EXISTING]: "Appointment on existing investment",
};

// Product options for proposal — alphabetical.
export const PRODUCT_OPTIONS = [
  "Endowment",
  "Linked Investment",
  "Living Annuity",
  "Preservation Fund",
  "Retirement Annuity",
  "Tax Free Savings Account",
];

// --- Helpers --------------------------------------------------------------
export const fmtZAR = (n) =>
  "R" + Math.round(Number(n)).toLocaleString("en-ZA", { maximumFractionDigits: 0 });

export const fmtPct = (n, digits = 2) =>
  (Number(n) * 100).toLocaleString("en-ZA", { minimumFractionDigits: digits, maximumFractionDigits: digits }) + "%";

export const todayStr = () => formatLongDate(new Date());

// --- Ongoing rate resolver ------------------------------------------------
// Pure function: decides the ongoing advice-fee rate for the "any other
// fund" path (special funds are handled before this is called).
//
//   adviserShareMonthly = ONGOING_RATE_STANDARD × base × splitPct / 100 / 12
//   bumpFires           = splitPct < 70  AND  adviserShareMonthly < hourlyRate × ONGOING_RETAIN_HOURS_PER_MONTH
//   rate                = bumpFires ? 0.50% : 0.25%
//
// The test always evaluates the adviser's share AT 0.25% — the bumped rate
// never enters the comparison. Equality retains 0.25%. Everything is
// ex-VAT, so no VAT adjustment is needed. A base of 0 (no amount entered
// yet) gives a share of 0, so the bump fires exactly as it always has.
//
// Guard: if hourlyRate is not a positive finite number the retention test
// is skipped and today's behaviour (bump on split < 70) applies. Never
// throws.
//
// Returns { rate, display, reasonCode } — see ONGOING_REASON. A split of 0
// or less returns rate 0 with reasonCode null (the "enter your split" state).
export function resolveOngoingRate({ base, splitPct, hourlyRate }) {
  const split = Number(splitPct) || 0;
  if (split <= 0) {
    return { rate: 0, display: "", reasonCode: null };
  }
  if (split >= ONGOING_SPLIT_THRESHOLD_PCT) {
    return { rate: ONGOING_RATE_STANDARD, display: ONGOING_RATE_STANDARD_DISPLAY, reasonCode: ONGOING_REASON.STANDARD };
  }

  const hourly = Number(hourlyRate);
  const hourlyRateUsable = Number.isFinite(hourly) && hourly > 0;
  const investmentValue = Number(base) || 0;
  const adviserShareMonthly = (ONGOING_RATE_STANDARD * investmentValue * (split / 100)) / 12;
  const oneHourPerMonth = hourlyRateUsable ? hourly * ONGOING_RETAIN_HOURS_PER_MONTH : Infinity;

  if (adviserShareMonthly >= oneHourPerMonth) {
    return { rate: ONGOING_RATE_STANDARD, display: ONGOING_RATE_STANDARD_DISPLAY, reasonCode: ONGOING_REASON.RETAINED };
  }
  return { rate: ONGOING_RATE_BUMPED, display: ONGOING_RATE_BUMPED_DISPLAY, reasonCode: ONGOING_REASON.BUMPED };
}

export const ONGOING_REASON_TEXT = {
  [ONGOING_REASON.STANDARD]: "70% split — standard rate.",
  [ONGOING_REASON.BUMPED]: "Commission split below 70% — ongoing rate increased to 0.50%.",
  [ONGOING_REASON.RETAINED]:
    "0.25% retained: your share already covers one hour per month at the current hourly rate.",
};

// --- Fee calculation engine -----------------------------------------------
// An investment can have a lump sum component, a recurring (debit order)
// component, or both. Initial fee only ever applies to a lump sum portion
// that meets the minimum threshold. Ongoing fee uses the year-1 base:
// lump sum + (monthly contribution × 12).
export function calculateFees(inputs, settings) {
  const {
    hasLumpSum,
    hasRecurring,
    lumpSumAmount,      // number
    monthlyAmount,      // number (per month)
    hours,              // number
    splitPct,           // 0..100
    specialFund,        // SPECIAL_FUNDS value
    transferType,       // TRANSFER_TYPES value
  } = inputs;

  const { hourlyRate, minThreshold, feeCap, vatRate } = settings;
  const isExempt = transferType && transferType !== TRANSFER_TYPES.NONE;

  const result = {
    initial: { applies: false, reason: "", gross: 0, vat: 0, total: 0, takeHome: 0, capped: false, uncappedGross: 0 },
    ongoing: { rate: 0, display: "", reason: "", reasonCode: null },
  };

  // ---- INITIAL FEE ----
  // Recurring contributions never attract initial fee. Initial fee logic
  // looks at the lump sum portion only. Regulated transfers (Section 14
  // and Directive 135) are legislatively exempt; appointments on existing
  // investments are exempt because no new money is being invested.
  if (!hasLumpSum) {
    result.initial.reason = hasRecurring
      ? "Recurring contributions only — no initial fee."
      : "No investment selected.";
  } else if (isExempt) {
    result.initial.reason =
      transferType === TRANSFER_TYPES.SECTION14
        ? "Section 14 transfer — no initial fee permitted (Pension Funds Act, s.14)."
        : transferType === TRANSFER_TYPES.DIRECTIVE135
        ? "Directive 135 transfer — no initial fee permitted (FSCA Directive 135)."
        : "Appointment on existing investment — no initial fee (no new money invested).";
  } else if (!lumpSumAmount || lumpSumAmount < minThreshold) {
    result.initial.reason = `Lump sum below the minimum threshold of ${fmtZAR(minThreshold)}. No initial fee.`;
  } else if (!hours || !splitPct) {
    result.initial.reason = "Enter hours billed and your split % to calculate.";
  } else {
    const split = splitPct / 100;
    const targetEarnings = hours * hourlyRate;
    const uncappedGross = targetEarnings / split;
    const maxGross = lumpSumAmount * feeCap; // cap is on the lump sum portion only
    const gross = Math.min(uncappedGross, maxGross);
    const capped = uncappedGross > maxGross;

    result.initial.applies = true;
    result.initial.uncappedGross = uncappedGross;
    result.initial.gross = gross;
    result.initial.vat = gross * vatRate;
    result.initial.total = gross * (1 + vatRate);
    result.initial.takeHome = gross * split;
    result.initial.capped = capped;
    result.initial.targetEarnings = targetEarnings;
    result.initial.lumpSumAmount = lumpSumAmount;
    result.initial.reason = capped
      ? `Capped at ${fmtPct(feeCap, 0)} of the lump sum (${fmtZAR(lumpSumAmount)}). Your earnings reduced from ${fmtZAR(targetEarnings)} to ${fmtZAR(result.initial.takeHome)}.`
      : "Calculated from hours × hourly rate ÷ split.";
  }

  // ---- ONGOING FEE ----
  // Year-1 base is computed FIRST because the split rule below needs it:
  // lump sum + (monthly × 12). Zero when nothing has been entered.
  const lumpPart = hasLumpSum ? (lumpSumAmount || 0) : 0;
  const recurringPart = hasRecurring ? (monthlyAmount || 0) * 12 : 0;
  const ongoingBase = (hasLumpSum || hasRecurring) ? lumpPart + recurringPart : 0;

  if (specialFund === SPECIAL_FUNDS.INCOME) {
    result.ongoing.rate = SPECIAL_FUND_RATES[SPECIAL_FUNDS.INCOME].rate;
    result.ongoing.display = SPECIAL_FUND_RATES[SPECIAL_FUNDS.INCOME].display;
    result.ongoing.reason = "100% Optimum BCI Income Fund — special rate applies regardless of split.";
  } else if (specialFund === SPECIAL_FUNDS.EDGE) {
    result.ongoing.rate = SPECIAL_FUND_RATES[SPECIAL_FUNDS.EDGE].rate;
    result.ongoing.display = SPECIAL_FUND_RATES[SPECIAL_FUNDS.EDGE].display;
    result.ongoing.reason = "100% OIG Capital Edge Model Portfolio — special rate applies regardless of split.";
  } else {
    const resolved = resolveOngoingRate({ base: ongoingBase, splitPct, hourlyRate });
    result.ongoing.rate = resolved.rate;
    result.ongoing.display = resolved.display;
    result.ongoing.reasonCode = resolved.reasonCode;
    result.ongoing.reason = resolved.reasonCode
      ? ONGOING_REASON_TEXT[resolved.reasonCode]
      : "Enter your split % to determine ongoing fee.";
  }

  // Year-1 ongoing fee estimate on the base computed above.
  if (result.ongoing.rate > 0 && ongoingBase > 0) {
    result.ongoing.base = ongoingBase;
    result.ongoing.lumpPart = lumpPart;
    result.ongoing.recurringPart = recurringPart;
    result.ongoing.combined = hasLumpSum && hasRecurring;
    result.ongoing.annualGross = ongoingBase * result.ongoing.rate;
    result.ongoing.annualVat = result.ongoing.annualGross * vatRate;
    result.ongoing.annualTotal = result.ongoing.annualGross * (1 + vatRate);
    result.ongoing.monthlyTotal = result.ongoing.annualTotal / 12;
    // Your portion of the gross ongoing fee (VAT goes to SARS, not the split).
    const ongoingSplit = (splitPct || 0) / 100;
    result.ongoing.takeHomeAnnual = result.ongoing.annualGross * ongoingSplit;
    result.ongoing.takeHomeMonthly = result.ongoing.takeHomeAnnual / 12;
  }

  return result;
}
